// Hlavička a dlaždice karty klientky (mockup „Karta klientky a dlaždice“, varianta A,
// schváleno 2. 10. 2026). Počítač i telefon z jednoho místa, ClientDetail jen
// dopočítá hodnoty a předá je sem.
//
// Hlavička: fotka nebo iniciály, jméno se stavem, údaje v jednom řádku oddělené
// svislou čarou, pod nimi jen to, co je potřeba řešit. Akce (poslat sales,
// přejmenovat, archivovat, smazat) jsou pod třemi tečkami, každý vidí jen to,
// co smí. Dlaždice: ikona, název, velké číslo vždy na stejném místě a detaily
// jako štítky s popiskem a hodnotou oddělenými svislou čarou.
import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { NabidkaAkci } from "./ui.jsx";
import InfoTip from "./InfoTip.jsx";
import { apiFetch } from "../lib/api.js";
import { ton as tonPodleKlice } from "../lib/tony.js";

// Ikona dlaždice: o stupeň sytější podklad než štítek, ať se čtvereček neztratí.
const IKONA_TON = {
  neutralni: "bg-slate-100 text-slate-600",
  znacka: "bg-brand-100 text-brand-deep",
  ok: "bg-emerald-100 text-emerald-700",
  pozor: "bg-amber-100 text-amber-700",
  problem: "bg-rose-100 text-rose-700",
  info: "bg-sky-100 text-sky-700",
};
const HODNOTA_TON = {
  ok: "text-emerald-700",
  pozor: "text-amber-700",
  problem: "text-rose-700",
};
function iniciala(jmeno) {
  return String(jmeno || "").trim().split(/\s+/).filter(Boolean).slice(0, 2).map((x) => x[0].toUpperCase()).join("") || "?";
}

// Profilová fotka z klientské aplikace, bez fotky iniciály na stejném místě,
// ať se hlavička neposouvá.
export function FotkaKlientky({ klientId, jmeno, velka = true }) {
  const [foto, setFoto] = useState(null);
  useEffect(() => {
    if (!klientId) return undefined;
    let zruseno = false;
    apiFetch(`/api/client-photo?klient_id=${encodeURIComponent(klientId)}`)
      .then((r) => r.json())
      .then((j) => { if (!zruseno && j.ok && j.foto) setFoto(j.foto); })
      .catch(() => {});
    return () => { zruseno = true; };
  }, [klientId]);
  const rozmer = velka ? "h-[76px] w-[76px]" : "h-16 w-16";
  if (foto) {
    return <img src={foto} alt={jmeno || "Profilová fotka"} title="Profilová fotka z klientské aplikace"
      className={`${rozmer} shrink-0 rounded-full border-[3px] border-white/85 object-cover shadow-lg demo-sensitive`} />;
  }
  return (
    <span aria-hidden className={`${rozmer} flex shrink-0 items-center justify-center rounded-full border-2 border-white/35 bg-white/10 font-bold text-white ${velka ? "text-[26px]" : "text-[22px]"}`}>
      {iniciala(jmeno)}
    </span>
  );
}

// Mřížka údajů pod jménem (varianta D2, 2. 10. 2026): popisek nahoře, hodnota pod ním,
// sloupce oddělené čarou. Co je potřeba řešit, se obarví přímo v buňce (ton "problem"
// červeně, "pozor" žlutě), takže hlavička se kvůli tomu neprodlužuje.
const BUNKA_TON = {
  problem: { bunka: "bg-rose-500/15", text: "text-rose-200", tecka: "bg-rose-400" },
  pozor: { bunka: "bg-brand-gold/10", text: "text-amber-100", tecka: "bg-amber-300" },
};
function MrizkaUdaju({ udaje }) {
  const polozky = udaje.filter((u) => u && u.hodnota);
  if (!polozky.length) return null;
  return (
    <div className="flex overflow-hidden rounded-2xl border border-white/15 bg-white/5">
      {polozky.map((u, i) => {
        const t = BUNKA_TON[u.ton];
        return (
          <div key={u.popisek} title={u.title || undefined}
            className={`min-w-0 flex-auto px-3 py-2.5 ${i > 0 ? "border-l border-white/15" : ""} ${t ? t.bunka : ""}`}>
            <div className="truncate text-[11px] font-bold uppercase tracking-[0.06em] text-white/60">{u.popisek}</div>
            <div className={`mt-0.5 text-[15px] font-semibold leading-snug [overflow-wrap:anywhere] ${t ? t.text : "text-white"}`}>
              {t && <span aria-hidden className={`mr-2 inline-block h-[7px] w-[7px] rounded-full align-middle ${t.tecka}`} />}
              {u.hodnota}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Hlavička karty klientky na počítači (na telefonu zůstává původní, Láďa 2. 10. 2026).
 * udaje: [{ popisek, hodnota, ton?: "problem" | "pozor", title? }]
 * stitky: štítky vedle stavu u jména (např. faktura po splatnosti).
 * akce: položky pro NabidkaAkci (prázdné pole = tři tečky se neukážou).
 */
export function HlavickaKlientky({
  headerClass, jmeno, klientId, stav, stitky = [], udaje = [],
  blacklist = null, akce = [], onClose, eyebrow = "Detail klientky",
}) {
  return (
    <div className={`${headerClass} px-6 py-5 text-white sm:px-7 sm:py-6`}>
      <div className="flex items-center gap-3">
        <span className="text-xs font-bold uppercase tracking-[0.08em] text-white/65">{eyebrow}</span>
        {blacklist}
        <div className="ml-auto flex items-center gap-2">
          {akce.length > 0 && <NabidkaAkci polozky={akce} tmava popis="Další akce" />}
          <button type="button" onClick={onClose} aria-label="Zavřít"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/20 bg-white/10 transition hover:bg-white/20 sm:h-10 sm:w-10">
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-5">
        <FotkaKlientky klientId={klientId} jmeno={jmeno} />
        <div className="min-w-0 flex-1 space-y-3.5">
          <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2">
            <h2 className="text-3xl font-bold leading-tight demo-sensitive">{jmeno}</h2>
            {stav}{stitky}
          </div>
          <MrizkaUdaju udaje={udaje} />
        </div>
      </div>
    </div>
  );
}

// Štítek s popiskem a hodnotou oddělenými svislou čarou.
function Pilulka({ ton = "neutralni", casti = [] }) {
  const t = tonPodleKlice(ton);
  const c = casti.filter((x) => x !== null && x !== undefined && x !== "");
  return (
    <span className={`inline-flex h-[26px] items-center gap-1.5 whitespace-nowrap rounded-full px-2 text-[12.5px] font-semibold ${t.pilulka}`}>
      {c.map((x, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span aria-hidden className="h-3 w-px bg-current opacity-35" />}
          {i > 0 ? <b className="font-bold">{x}</b> : <span>{x}</span>}
        </React.Fragment>
      ))}
    </span>
  );
}

function Hodnota({ d }) {
  const dlouha = typeof d.hodnota === "string" && d.hodnota.length > 6;
  return (
    <div className={`whitespace-nowrap font-bold leading-none tabular-nums tracking-tight ${dlouha ? "text-[24px] sm:text-[26px]" : "text-[28px] sm:text-[34px]"} ${HODNOTA_TON[d.hodnotaTon] || "text-slate-900"}`}>
      {d.hodnota}
      {d.jednotka && <span className="ml-1.5 text-[14px] font-semibold tracking-normal text-slate-500 sm:text-[15px]">{d.jednotka}</span>}
    </div>
  );
}

function IkonaDlazdice({ d, mala }) {
  const Ikona = d.ikona;
  return (
    <span className={`flex shrink-0 items-center justify-center ${mala ? "h-9 w-9 rounded-[11px]" : "h-[42px] w-[42px] rounded-[13px]"} ${IKONA_TON[d.ikonaTon] || IKONA_TON.neutralni}`}>
      {Ikona && <Ikona className={mala ? "h-[18px] w-[18px]" : "h-5 w-5"} />}
    </span>
  );
}

/**
 * Dlaždice karty klientky.
 * dlazdice: [{ klic, ikona, ikonaTon, popisek, napoveda, hodnota, jednotka, hodnotaTon,
 *              pilulky: [{ ton, casti: [popisek, hodnota] }] }]
 * Jen počítač, na telefonu zůstávají řádky (Láďa 2. 10. 2026).
 */
export function DlazdiceKlientky({ dlazdice = [] }) {
  const karta = "flex flex-col rounded-[20px] border border-slate-200 bg-white shadow-glass";
  return (
    <div className={`grid gap-3.5 ${dlazdice.length >= 5 ? "grid-cols-2 md:grid-cols-3 lg:grid-cols-5" : "grid-cols-2 md:grid-cols-3"}`}>
      {dlazdice.map((d) => (
        <div key={d.klic} className={`${karta} min-h-[212px] p-[18px]`}>
          <div className="flex items-start justify-between">
            <IkonaDlazdice d={d} />
            {d.napoveda && <span className="-mr-1 -mt-1"><InfoTip text={d.napoveda} /></span>}
          </div>
          <div className="mt-3 truncate text-[14px] font-semibold text-slate-700">{d.popisek}</div>
          <div className="mt-1.5"><Hodnota d={d} /></div>
          {d.pilulky?.length > 0 && <div className="mt-auto flex flex-wrap gap-1.5 pt-3">{d.pilulky.map((p, i) => <Pilulka key={i} {...p} />)}</div>}
        </div>
      ))}
    </div>
  );
}
